import { Button, DatePicker, Form, Input, Radio, Select, Space, Typography, Popconfirm, Col, Grid, theme } from 'antd';
import { APOLLO_DATE_FORMAT, DATE_FORMAT } from '../../../../../../../../constants';
import { PaymentContainer } from '../../../../../components/payment.container';
import { Country } from '../../../../../../../../types/country';
import { CredentialVerificationCaseV1Application, CredentialVerificationCaseV1ApplicationInput, Entity } from '../../../../../../../../generated';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';
import { UploadNameOnDocumentContainer } from './upload-name-on-document.container';
import { CredCaseFileUploadButtonWithPreviewContainer } from '../../../../../../../shared/file-upload-button-with-preview/cred-case-file-upload-button-with-preview.container';
import { Content } from 'antd/es/layout/layout';
import { CertificationByApplicantLegalDisclaimer } from '../../../../../components/certification-by-applicant-legal-disclaimer';
import utc from 'dayjs/plugin/utc';
import { StateSelect } from '../../../../../shared/components/state-select';
dayjs.extend(utc);

interface CreateProps {
  caseData: CredentialVerificationCaseV1Application;
  countryData: Country[];
  entities: Entity[];
  serviceFee: number;
  caseId: string;
  applicantUserId: string;
  onSubmit: (values: CredentialVerificationCaseV1ApplicationInput) => Promise<void>;
  handleOnUploadSuccess: (response: AzureBlobResponse, fieldName: string) => Promise<void>;
  onWithdrawRequest: () => Promise<void>;
  formLoading: boolean;
}

export const Create: React.FC<CreateProps> = (props) => {
  const [form] = Form.useForm();
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [showOtherInstitutionDetails, setShowOtherInstitutionDetails] = useState<boolean>(props.caseData?.issuingInstitution?.id === 'Other');
  const screen = Grid.useBreakpoint();
  const isMobile = screen.xs;

  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();

  useEffect(() => {
    if (props.caseData?.credentialAssets?.credential) {
      form.setFieldsValue({
        uploadCredential: 'uploaded'
      });
    }
  }, [props.caseData?.credentialAssets?.credential]);

  const handleSubmitRequest = async () => {
    form
      .validateFields()
      .then((values) => {
        delete values.uploadCredential;
        delete values.uploadCredentialTranslation;
        if (values.issuingInstitution === 'other') {
          values.issuingInstitution = null;
        }
        values.isAttestationComplete = values.informationAuthorization && values.certificationAgreement && values.termAcknowledgement;
        values.dateCredentialIssuedAt = dayjs(values.dateCredentialIssuedAt).format(APOLLO_DATE_FORMAT);
        delete values.termsAndConditions;
        props.onSubmit(values);
      })
      .catch((error) => {
        console.log('Error', error);
      });
  };

  const initialValues: CredentialVerificationCaseV1Application = {
    credentialType: props.caseData?.credentialType ?? 'primary',
    credentialTitle: props.caseData?.credentialTitle,
    isCredentialInEnglish: props.caseData?.isCredentialInEnglish ?? true,
    program: props.caseData?.program,
    nameOnCredential: props.caseData?.nameOnCredential,
    dateCredentialIssuedAt: props.caseData?.dateCredentialIssuedAt,
    issuingInstitution: props.caseData?.issuingInstitution?.id,
    otherIssuingInstitution: {
      institutionName: props.caseData?.otherIssuingInstitution?.institutionName,
      addressLine1: props.caseData?.otherIssuingInstitution?.addressLine1,
      country: props.caseData?.otherIssuingInstitution?.country,
      city: props.caseData?.otherIssuingInstitution?.city,
      stateOrProvince: props.caseData?.otherIssuingInstitution?.stateOrProvince,
      zipOrPostalCode: props.caseData?.otherIssuingInstitution?.zipOrPostalCode
    },
    sendDestination: props.caseData?.sendDestination?.id,
    isNameOnCredentialMatchPassport: props.caseData?.isNameOnCredentialMatchPassport ?? true,
    isAttestationComplete: props.caseData?.isAttestationComplete
  };
  return (
    <Content
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          background: colorBgContainer,
          width: isMobile ? '100%' : '70%',
          minWidth: 280,
          padding: isMobile ? '48px 0px 0px 0px' : '48px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'baseline'
        }}
      >
        <Form form={form} style={{ width: isMobile ? '100%' : '70%' }} layout="vertical" initialValues={initialValues}>
          <Form.Item
            name={['credentialType']}
            valuePropName="value"
            getValueProps={(value) => ({
              value: value
            })}
            rules={[
              {
                required: true
              }
            ]}
            label={
              <Typography.Paragraph style={isMobile ? { fontSize: '14px', fontWeight: '500' } : { fontSize: '18px', fontWeight: '500' }}>
                What type of credential are you requesting to be verified?{' '}
              </Typography.Paragraph>
            }
          >
            <Radio.Group defaultValue={'primary'} size="large" style={{ marginLeft: '10px' }}>
              <Space direction="vertical" style={{ rowGap: '0px' }}>
                <Radio style={isMobile ? { fontSize: '16px', fontWeight: 'bold' } : { fontSize: '20px', fontWeight: 'bold' }} value="primary">
                  Primary Professional Credential{' '}
                </Radio>
                <Typography.Paragraph style={{ marginLeft: '24px' }}>
                  This is the credential issued by your educational institution after you have completed all requirements for and been awarded your degree qualifying you to
                  practice in your profession. If your credential is not uploaded in the appropriate document category, ECFMG may correct the document category.
                </Typography.Paragraph>
                <Radio style={isMobile ? { fontSize: '16px', fontWeight: 'bold' } : { fontSize: '20px', fontWeight: 'bold' }} value="other">
                  Other Credential
                </Radio>
                <Typography.Paragraph style={{ marginLeft: '24px' }}>
                  This is any other credential type related to your professional practice that you may need to have verified. Examples include transcripts, specialty
                  qualifications, licenses, or other credentials.
                </Typography.Paragraph>
              </Space>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name={['uploadCredential']}
            rules={[
              {
                required: true,
                message: 'Please upload credential file'
              }
            ]}
            label={<Typography.Paragraph style={{ fontSize: '18px', fontWeight: '500' }}>Upload Credential</Typography.Paragraph>}
          >
            <CredCaseFileUploadButtonWithPreviewContainer
              caseId={props.caseId}
              title={''}
              buttonText={'Upload Credential'}
              permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
              permittedExtensions={['pdf', 'jpeg', 'jpg', 'png']}
              fieldName={'credential'}
              fileName={'credential'}
              isShowFileName={false}
              onUploadSuccess={(response) => {
                form.setFieldValue('uploadCredential', 'uploaded');
                props.handleOnUploadSuccess(response, 'credential');
                form.validateFields(['uploadCredential']);
              }}
              cropAllowed={false}
            />
          </Form.Item>
          <Form.Item
            name={['isCredentialInEnglish']}
            getValueProps={(value) => {
              return {
                value: value
              };
            }}
            valuePropName="value"
            label={
              <>
                <Typography.Paragraph style={isMobile ? { fontSize: '16px', fontWeight: 'bold' } : { fontSize: '18px', fontWeight: '500' }}>
                  Is the credential you uploaded in English?
                </Typography.Paragraph>
              </>
            }
            rules={[
              {
                required: true,
                message: 'Please select an option'
              }
            ]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Typography.Paragraph>
              Any document you upload that is not in English must be accompanied by an English translation that meets{' '}
              <a href="https://www.ecfmg.org/resources/english-translations.html" target="_blank" rel="noopener noreferrer">
              ECFMG's translation requirements
              </a>.
            </Typography.Paragraph>
            <Radio.Group defaultValue={props.caseData?.isCredentialInEnglish ?? true} size="large" style={{ marginLeft: '10px' }}>
              <Space direction="vertical" style={{ rowGap: '0px' }}>
                <Radio value={true} onChange={() => form.setFieldValue('isCredentialInEnglish', true)}>
                  <Typography.Text strong>Yes.</Typography.Text>
                </Radio>
                <Radio value={false} onChange={() => form.setFieldValue('isCredentialInEnglish', false)}>
                  <Typography.Text strong>No.</Typography.Text> I will provide an English translation that meets ECFMG's translation requirements.
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {!Form.useWatch('isCredentialInEnglish', form) && (
            <Form.Item
              name={['uploadCredentialTranslation']}
              rules={[
                {
                  required: true,
                  message: 'Please upload translation'
                }
              ]}
            >
              <CredCaseFileUploadButtonWithPreviewContainer
                caseId={props.caseId}
                title={''}
                buttonText={'Upload English Translation'}
                permittedContentTypes={['application/pdf', 'image/jpeg', 'image/png']}
                permittedExtensions={['pdf', 'jpeg', 'jpg', 'png']}
                fieldName={'translation'}
                fileName={'translation'}
                isShowFileName={false}
                onUploadSuccess={(response) => {
                  form.setFieldValue('uploadCredentialTranslation', 'uploaded');
                  props.handleOnUploadSuccess(response, 'translation');
                  form.validateFields(['uploadCredentialTranslation']);
                }}
              />
            </Form.Item>
          )}
          <Typography.Paragraph style={{ fontSize: '18px', fontWeight: '500' }}>Credential Details</Typography.Paragraph>
          <div
            style={{
              display: 'flex',
              gap: '10px'
            }}
          >
            <Form.Item
              label="Credential Title"
              name="credentialTitle"
              rules={[
                {
                  required: true,
                  message: 'Please enter Credential Title'
                }
              ]}
            >
              <Input placeholder="Credential Title" />
            </Form.Item>
            <Form.Item label="Program (If Applicable)" name="program">
              <Input placeholder="Program" />
            </Form.Item>
          </div>
          <div
            style={{
              display: 'flex',
              gap: '10px'
            }}
          >
            <Form.Item
              label="Name on credential"
              name="nameOnCredential"
              rules={[
                {
                  required: true,
                  message: 'Please enter Name on Credential'
                }
              ]}
            >
              <Input placeholder="Name on Credential" />
            </Form.Item>
            <Form.Item
              label="Date Credential Issued"
              name="dateCredentialIssuedAt"
              rules={[
                {
                  required: true,
                  message: 'Please select Credential Issued Date'
                }
              ]}
              getValueProps={(value) => ({
                value: value ? dayjs(value).utc() : ''
              })}
            >
              <DatePicker
                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                maxDate={dayjs().utc()}
                value={props?.caseData?.dateCredentialIssuedAt ? dayjs(props?.caseData?.dateCredentialIssuedAt).utc() : ''}
                style={{ width: 200 }}
              />
            </Form.Item>
          </div>
          <Typography.Paragraph style={{ fontSize: '18px', fontWeight: '500' }}>Institution that Issued the Credential </Typography.Paragraph>
          <Typography.Paragraph>
            <Typography.Text strong>Please Note: </Typography.Text> You may be responsible for additional charges as some institutions require verification fees.{' '}
          </Typography.Paragraph>

          <Form.Item
            label="Issuing Institution"
            name={['issuingInstitution']}
            rules={[
              {
                required: true,
                message: 'Please select Issuing Institution'
              }
            ]}
          >
            <Select
              style={{ width: '100%' }}
              placeholder="Select Issuing Institution"
              onChange={(value) => {
                if (value === 'other') {
                  setShowOtherInstitutionDetails(value === 'other');
                } else {
                  setShowOtherInstitutionDetails(false);
                }
              }}
            >
              {props.entities
                .filter((entity) => entity.isIssuingInstitution)
                .map((entity) => (
                  <Select.Option key={entity.id} value={entity.id}>
                    {entity.entityName}
                  </Select.Option>
                ))}
              <Select.Option key="other" value="other">
                Other
              </Select.Option>
            </Select>
          </Form.Item>
          {showOtherInstitutionDetails && (
            <>
              <Form.Item
                label="Institution Name"
                name={['otherIssuingInstitution', 'institutionName']}
                rules={[
                  {
                    required: true,
                    message: 'Please enter Institution Name'
                  }
                ]}
              >
                <Input style={{ width: '100%' }} placeholder="Institution Name" />
              </Form.Item>
              <Form.Item
                label="Address"
                name={['otherIssuingInstitution', 'addressLine1']}
                rules={[
                  {
                    required: true,
                    message: 'Please enter Address'
                  }
                ]}
              >
                <Input style={{ width: 500 }} placeholder="Address" />
              </Form.Item>
              <Form.Item
                label="Country"
                name={['otherIssuingInstitution', 'country']}
                rules={[
                  {
                    required: true,
                    message: 'Please select Country'
                  }
                ]}
              >
                <Select style={{ width: 400 }} placeholder="Select Country" showSearch onChange={(value) => setSelectedCountry(value)} optionFilterProp="children">
                  {props.countryData.map((item: Country) => (
                    <Select.Option key={item.countryCode} value={item.countryCode}>
                      {item.countryName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  flexDirection: isMobile ? 'column' : 'row'
                }}
              >
                <Form.Item label="City" name={['otherIssuingInstitution', 'city']}>
                  <Input style={{ width: 150 }} placeholder="City" />
                </Form.Item>
                <Form.Item>
                  <StateSelect
                    name={['otherIssuingInstitution', 'stateOrProvince']}
                    selectedCountry={selectedCountry} // Pass the corresponding country
                    countryData={props.countryData}
                  />
                </Form.Item>
                <Form.Item
                  label="Zip / Postal Code"
                  name={['otherIssuingInstitution', 'zipOrPostalCode']}
                  rules={[
                    {
                      required: true
                    }
                  ]}
                >
                  <Input style={{ width: 150 }} maxLength={10} placeholder="Zip / Postal Code" />
                </Form.Item>
              </div>
            </>
          )}
          <Form.Item
            name={['isNameOnCredentialMatchPassport']}
            getValueProps={(value) => {
              return {
                value: value
              };
            }}
            valuePropName="value"
            label={
              <Typography.Paragraph style={isMobile ? { fontSize: '14px', fontWeight: '500' } : { fontSize: '18px', fontWeight: '500' }}>
                Does the name on your credential match the name in your Profile?{' '}
              </Typography.Paragraph>
            }
            rules={[
              {
                required: true,
                message: 'Please select an option'
              }
            ]}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Radio.Group defaultValue={props.caseData?.isNameOnCredentialMatchPassport ?? true} size="large" style={{ marginLeft: '10px' }}>
              <Space direction="vertical" style={{ rowGap: '0px' }}>
                <Radio value={true}>
                  <Typography.Text strong>Yes.</Typography.Text>
                </Radio>
                <Radio value={false}>
                  <Typography.Text strong>No,</Typography.Text> I am providing documentation to confirm that both names belong to me.
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {!Form.useWatch('isNameOnCredentialMatchPassport', form) && (
            <Form.Item name={['uploadNameOnDocument']}>
              <UploadNameOnDocumentContainer
                title={''}
                description={''}
                applicantUserId={props.applicantUserId}
                fileName={'nameOnDocument.pdf'}
                isShowFileName={false}
                permittedContentTypes={['image/jpeg', 'application/pdf']}
                permittedExtensions={['jpg', 'jpeg', 'pdf']}
                buttonText="Upload Documentation to Confirm Name"
              />
            </Form.Item>
          )}
          <Form.Item
            label={
              <Typography.Paragraph style={isMobile ? { fontSize: '14px', fontWeight: '500' } : { fontSize: '18px', fontWeight: '500' }}>
                Please select a recipient for the Verification Status Report on the credential.
              </Typography.Paragraph>
            }
            name={['sendDestination']}
            rules={[
              {
                required: true,
                message: 'Please select Destination'
              }
            ]}
          >
            <Select style={{ width: '100%' }} placeholder="Select Destination">
              {props.entities
                .filter((entity) => entity.isClient)
                .map((entity) => (
                  <Select.Option key={entity.id} value={entity.id}>
                    {entity.entityName}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <CertificationByApplicantLegalDisclaimer />
          <PaymentContainer isDraftMode={true} />
          <br />
            <Typography.Text strong>Your credit card will be charged US${props.serviceFee} when you submit your request.</Typography.Text>
        </Form>
      </div>
      <br />
      <div className={`flex items-center ${isMobile ? 'justify-center' : 'justify-end'} gap-4 w-full`}>
        <Col>
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={async () => await props.onWithdrawRequest()}>
            <Button danger disabled={props.formLoading}>
              Withdraw Request
            </Button>
          </Popconfirm>
        </Col>

        <Popconfirm title="Are you sure?" onConfirm={handleSubmitRequest}>
          <Button type="primary" loading={props.formLoading}>
            Submit Request
          </Button>
        </Popconfirm>
      </div>
    </Content>
  );
};
